import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

// Month names for UI
export const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

// Format duration for display
export const formatDuration = (durationSeconds) => {
  if (!durationSeconds) return 'Automatycznie';
  const days = Math.floor(durationSeconds / (24 * 60 * 60));
  return `${days} dni`;
};

// Format months for display
export const formatMonths = (months) => {
  if (!months || months.length === 0) return 'Wszystkie miesiące';
  return months.map(m => monthNames[m - 1]).join(', ');
};

// Format purchase period for display
export const formatPurchasePeriod = (passType) => {
  if (!passType.purchase_period_restricted) return 'Zawsze dostępny';

  const from = passType.purchase_available_from ?
    new Date(passType.purchase_available_from).toLocaleDateString() : '';
  const until = passType.purchase_available_until ?
    new Date(passType.purchase_available_until).toLocaleDateString() : '';

  if (from && until) return `${from} - ${until}`;
  if (from) return `Od ${from}`;
  if (until) return `Do ${until}`;
  return 'Zawsze dostępny';
};

// Format lesson date for display
export const formatLessonDate = (dateString) => {
  const date = new Date(dateString);
  return format(date, 'dd.MM.yyyy HH:mm', { locale: pl });
};

// Format event date for display
export const formatEventDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return format(date, 'dd.MM.yyyy', { locale: pl });
  } catch {
    return dateString;
  }
};

// Initial form data for creating a new pass type
export const getInitialFormData = () => ({
  name: '',
  price: '',
  valid_duration_days: '',
  active: true,
  available_for_purchase: true,
  month_restricted: false,
  available_months: [],
  purchase_period_restricted: false,
  purchase_available_from: '',
  purchase_available_until: '',
  purchase_limit: '',
  lesson_categories_for_pass_type: [],
  is_event_pass: false,
  is_special: false,
  event_ids_for_event_pass: [],
});

// Convert pass type to form data for editing
export const passTypeToFormData = (passType) => ({
  name: passType.name,
  price: passType.price.toString(),
  valid_duration_days: passType.valid_duration ? Math.floor(passType.valid_duration / (24 * 60 * 60)).toString() : '',
  active: passType.active,
  available_for_purchase: passType.available_for_purchase !== undefined ? passType.available_for_purchase : true,
  month_restricted: passType.month_restricted || false,
  available_months: passType.available_months || [],
  purchase_period_restricted: passType.purchase_period_restricted || false,
  purchase_available_from: passType.purchase_available_from ?
    new Date(passType.purchase_available_from).toISOString().slice(0, 16) : '',
  purchase_available_until: passType.purchase_available_until ?
    new Date(passType.purchase_available_until).toISOString().slice(0, 16) : '',
  purchase_limit: passType.purchase_limit ? passType.purchase_limit.toString() : '',
  lesson_categories_for_pass_type: (passType.lesson_categories_for_pass_type || []).map(cat => ({
    ...cat,
    is_unlimited: cat.is_unlimited || false
  })),
  is_event_pass: passType.is_event_pass || false,
  is_special: passType.is_special || false,
  event_ids_for_event_pass: passType.events_for_event_pass 
    ? passType.events_for_event_pass.map(e => e.event_id) 
    : [],
});

// Convert form data to API payload
export const formDataToApiPayload = (formData) => {
  const apiData = {
    name: formData.name,
    price: parseInt(formData.price),
    active: formData.active,
    available_for_purchase: formData.available_for_purchase,
    month_restricted: formData.is_event_pass ? false : formData.month_restricted,
    available_months: formData.is_event_pass ? null : (formData.month_restricted ? formData.available_months : null),
    purchase_period_restricted: formData.purchase_period_restricted,
    purchase_available_from: formData.purchase_period_restricted && formData.purchase_available_from ?
      new Date(formData.purchase_available_from).toISOString() : null,
    purchase_available_until: formData.purchase_period_restricted && formData.purchase_available_until ?
      new Date(formData.purchase_available_until).toISOString() : null,
    purchase_limit: formData.purchase_limit ? parseInt(formData.purchase_limit) : null,
    is_event_pass: formData.is_event_pass,
    is_special: formData.is_special,
  };

  // Add type-specific fields
  if (formData.is_event_pass) {
    apiData.event_ids_for_event_pass = formData.event_ids_for_event_pass;
    apiData.valid_duration = formData.valid_duration_days ?
      parseInt(formData.valid_duration_days) * 24 * 60 * 60 : null;
    apiData.lesson_categories_for_pass_type = [];
  } else {
    apiData.valid_duration = formData.valid_duration_days ?
      parseInt(formData.valid_duration_days) * 24 * 60 * 60 :
      (formData.month_restricted ? null : 0);
    apiData.lesson_categories_for_pass_type = formData.lesson_categories_for_pass_type;
    apiData.event_ids_for_event_pass = null;
  }

  return apiData;
};

// Validate form data and return error message or null
export const validateFormData = (formData) => {
  if (!formData.name || !formData.price) {
    return 'Nazwa i cena są wymagane';
  }

  if (formData.is_event_pass) {
    if (!formData.event_ids_for_event_pass || formData.event_ids_for_event_pass.length === 0) {
      return 'Proszę wybrać co najmniej jeden event dla karnetu eventowego';
    }
  } else {
    if (!formData.month_restricted && !formData.valid_duration_days) {
      return 'Czas trwania jest wymagany gdy nie ma ograniczenia miesięcznego';
    }

    if (formData.month_restricted && formData.available_months.length === 0) {
      return 'Proszę wybrać co najmniej jeden miesiąc dla karnetów z ograniczeniem miesięcznym';
    }
  }

  if (formData.purchase_period_restricted) {
    if (!formData.purchase_available_from && !formData.purchase_available_until) {
      return 'Proszę określić okres dostępności zakupu';
    }
    if (formData.purchase_available_from && formData.purchase_available_until &&
        new Date(formData.purchase_available_from) >= new Date(formData.purchase_available_until)) {
      return 'Data rozpoczęcia musi być wcześniejsza niż data zakończenia';
    }
  }

  return null;
};
